import { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Input from '../../../components/ui/Input'
import SearchableSelect from '../../../components/ui/SearchableSelect'
import DoctorSearchSelect from '../../../components/ui/DoctorSearchSelect'
import ToothChart from '../../../components/dental/ToothChart'
import api from '../../../lib/api'
import { formatCurrency } from '../../../lib/formatters'
import type { Doctor } from '../../../types'

interface User {
  id: string
  name: string
  email: string
  role: string
}

interface CatalogItem {
  id: string
  name: string
  category: string
  basePrice: number
  hsnCode: string
}

interface OrderItem {
  catalogItemId: string
  catalogItem?: CatalogItem
  quantity: number
  unitPrice: number
  toothNumbers?: string
  selectedTeeth: string[]
  shade?: string
  notes?: string
}

export default function NewWorkOrderPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [errors, setErrors] = useState<Record<string, string>>({})
  
  // Form state
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null)
  const [selectedDoctorId, setSelectedDoctorId] = useState('')
  const [patientName, setPatientName] = useState('')
  const [patientPhone, setPatientPhone] = useState('')
  const [priority, setPriority] = useState('normal')
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0])
  const [dueDate, setDueDate] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [notes, setNotes] = useState('')
  const [orderItems, setOrderItems] = useState<OrderItem[]>([])
  const [expandedItemIndex, setExpandedItemIndex] = useState<number | null>(null)

  // Fetch catalog items
  const { data: catalogData } = useQuery({
    queryKey: ['catalog-items'],
    queryFn: async () => {
      const { data } = await api.get('/work-items?per_page=100')
      return data
    },
  })

  const catalogItems: CatalogItem[] = catalogData?.data || []

  // Fetch users for assignment
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      try {
        const { data } = await api.get('/users')
        return data
      } catch {
        return { data: [] }
      }
    },
  })

  const users: User[] = usersData?.data || []

  // Set default due date to 7 days from now
  useEffect(() => {
    const date = new Date()
    date.setDate(date.getDate() + 7)
    setDueDate(date.toISOString().split('T')[0])
  }, [])


  const addItem = () => {
    const newIndex = orderItems.length
    setOrderItems([...orderItems, {
      catalogItemId: '',
      quantity: 1,
      unitPrice: 0,
      selectedTeeth: [],
    }])
    setExpandedItemIndex(newIndex)
  }

  const updateItem = (index: number, updates: Partial<OrderItem>) => {
    const newItems = [...orderItems]
    newItems[index] = { ...newItems[index], ...updates }
    
    // If catalog item changed, update price
    if (updates.catalogItemId) {
      const item = catalogItems.find(c => c.id === updates.catalogItemId)
      if (item) {
        newItems[index].catalogItem = item
        newItems[index].unitPrice = item.basePrice
      }
    }
    
    setOrderItems(newItems)
  }

  const removeItem = (index: number) => {
    setOrderItems(orderItems.filter((_, i) => i !== index))
  }

  const calculateTotal = () => {
    return orderItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0)
  }

  const validateStep1 = () => {
    const newErrors: Record<string, string> = {}
    if (!selectedDoctor) {
      newErrors.doctor = 'Please select a doctor'
    }
    if (!dueDate) {
      newErrors.dueDate = 'Please select a due date'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateStep2 = () => {
    const newErrors: Record<string, string> = {}
    if (orderItems.length === 0) {
      newErrors.items = 'Please add at least one item'
    }
    const hasEmptyItem = orderItems.some(item => !item.catalogItemId)
    if (hasEmptyItem) {
      newErrors.items = 'Please select a work item for all entries'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const nextStep = () => {
    if (step === 1 && validateStep1()) {
      setStep(2)
    } else if (step === 2 && validateStep2()) {
      setStep(3)
    }
  }

  const [submitError, setSubmitError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        doctor_id: selectedDoctor?.id,
        patient_name: patientName || undefined,
        patient_phone: patientPhone || undefined,
        priority,
        received_date: receivedDate,
        due_date: dueDate,
        technician_id: assignedTo || undefined,
        notes: notes || undefined,
        items: orderItems.map(item => ({
          work_item_id: item.catalogItemId,
          quantity: item.quantity,
          unit_rate: item.unitPrice,
          tooth_numbers: item.selectedTeeth.join(', ') || undefined,
          shade_code: item.shade || undefined,
          notes: item.notes || undefined,
        })),
      }
      console.log('Submitting work order:', payload)
      return api.post('/work-orders', payload)
    },
    onSuccess: (response) => {
      console.log('Work order created:', response.data)
      try {
        const orderId = response.data?.data?.id
        console.log('Navigating to order:', orderId)
        if (orderId) {
          // Use window.location for reliable navigation after creation
          window.location.href = `/work-orders/${orderId}`
        } else {
          window.location.href = '/work-orders'
        }
      } catch (navError) {
        console.error('Navigation error:', navError)
        window.location.href = '/work-orders'
      }
    },
    onError: (error: any) => {
      console.error('Work order creation failed:', error)
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to create work order. Please try again.'
      setSubmitError(errorMessage)
    },
  })

  const handleSubmit = () => {
    mutation.mutate()
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">New Work Order</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Create a new work order for a doctor
          </p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/work-orders')}>
          <i className="fas fa-arrow-left mr-2"></i>
          Back
        </Button>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4">
        {[
          { num: 1, label: 'Doctor & Details' },
          { num: 2, label: 'Work Items' },
          { num: 3, label: 'Review & Submit' },
        ].map((s, idx) => (
          <div key={s.num} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s.num
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}
            >
              {step > s.num ? <i className="fas fa-check"></i> : s.num}
            </div>
            <span className={`ml-2 text-sm hidden sm:inline ${
              step >= s.num ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-500'
            }`}>
              {s.label}
            </span>
            {idx < 2 && (
              <div className={`w-12 h-0.5 mx-4 ${
                step > s.num ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
              }`} />
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Doctor & Details */}
      {step === 1 && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Doctor & Order Details
          </h2>
          
          <div className="space-y-4">
            {/* Doctor Selection */}
            <DoctorSearchSelect
              label="Select Doctor"
              value={selectedDoctorId}
              onChange={(doctorId, doctor) => {
                setSelectedDoctorId(doctorId)
                setSelectedDoctor(doctor || null)
                if (errors.doctor) setErrors({ ...errors, doctor: '' })
              }}
              error={errors.doctor}
              required
            />

            {/* Patient Info (Optional) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Patient Name (Optional)"
                value={patientName}
                onChange={(e) => setPatientName(e.target.value)}
                placeholder="Patient's name"
              />
              <Input
                label="Patient Phone (Optional)"
                value={patientPhone}
                onChange={(e) => setPatientPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="10-digit phone"
              />
            </div>

            {/* Received Date, Priority & Due Date */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input
                label="Received Date *"
                type="date"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
              />
              <SearchableSelect
                label="Priority"
                value={priority}
                onChange={setPriority}
                icon="fa-flag"
                searchable={false}
                options={[
                  { value: 'low', label: 'Low' },
                  { value: 'normal', label: 'Normal' },
                  { value: 'high', label: 'High' },
                  { value: 'urgent', label: 'Urgent' },
                ]}
                placeholder="Select priority..."
              />
              <Input
                label="Due Date *"
                type="date"
                value={dueDate}
                onChange={(e) => {
                  setDueDate(e.target.value)
                  if (errors.dueDate) setErrors({ ...errors, dueDate: '' })
                }}
                error={errors.dueDate}
              />
            </div>

            {/* Assign To */}
            <SearchableSelect
              label="Assign To"
              value={assignedTo}
              onChange={setAssignedTo}
              icon="fa-user"
              placeholder="Select technician..."
              options={[
                { value: '', label: 'Unassigned' },
                ...users.map(user => ({ 
                  value: user.id, 
                  label: user.name,
                  sublabel: user.role
                }))
              ]}
            />

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Special Instructions
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                placeholder="Any special instructions or notes..."
              />
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <Button onClick={nextStep}>
              Next: Add Items
              <i className="fas fa-arrow-right ml-2"></i>
            </Button>
          </div>
        </Card>
      )}

      {/* Step 2: Work Items */}
      {step === 2 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Work Items
            </h2>
            <Button variant="secondary" onClick={addItem}>
              <i className="fas fa-plus mr-2"></i>
              Add Item
            </Button>
          </div>

          {errors.items && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                <i className="fas fa-exclamation-circle"></i>
                {errors.items}
              </p>
            </div>
          )}

          {orderItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <i className="fas fa-box-open text-4xl mb-3"></i>
              <p>No items added yet</p>
              <p className="text-sm">Click "Add Item" to start adding work items</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orderItems.map((item, index) => (
                <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  {/* Item Header */}
                  <div 
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 cursor-pointer"
                    onClick={() => setExpandedItemIndex(expandedItemIndex === index ? null : index)}
                  >
                    <div className="flex items-center gap-3">
                      <i className={`fas fa-chevron-${expandedItemIndex === index ? 'down' : 'right'} text-gray-400 transition-transform`}></i>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Item #{index + 1}
                        {item.catalogItem && (
                          <span className="ml-2 text-gray-500">- {item.catalogItem.name}</span>
                        )}
                      </span>
                      {item.selectedTeeth.length > 0 && (
                        <span className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-600 text-xs rounded-full">
                          {item.selectedTeeth.length} teeth
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatCurrency(item.quantity * item.unitPrice)}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeItem(index); }}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                  
                  {/* Item Details (Expandable) */}
                  {expandedItemIndex === index && (
                    <div className="p-4 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="sm:col-span-2">
                          <SearchableSelect
                            label="Work Item *"
                            value={item.catalogItemId}
                            onChange={(val) => updateItem(index, { catalogItemId: val })}
                            icon="fa-box"
                            placeholder="Search work item..."
                            options={catalogItems.map((cat) => ({
                              value: cat.id,
                              label: cat.name,
                              sublabel: `${cat.category} • ${formatCurrency(cat.basePrice)}`,
                            }))}
                          />
                        </div>
                        
                        <Input
                          label="Quantity"
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, { quantity: parseInt(e.target.value) || 1 })}
                        />
                        
                        <Input
                          label="Unit Price (₹)"
                          type="number"
                          min="0"
                          value={item.unitPrice}
                          onChange={(e) => updateItem(index, { unitPrice: parseFloat(e.target.value) || 0 })}
                        />
                        
                        <Input
                          label="Shade"
                          value={item.shade || ''}
                          onChange={(e) => updateItem(index, { shade: e.target.value })}
                          placeholder="e.g., A2, B1"
                        />
                        
                        <div className="sm:col-span-3">
                          <Input
                            label="Notes"
                            value={item.notes || ''}
                            onChange={(e) => updateItem(index, { notes: e.target.value })}
                            placeholder="Any specific notes for this item"
                          />
                        </div>
                      </div>
                      
                      {/* Tooth Selection Chart */}
                      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          <i className="fas fa-tooth mr-2"></i>
                          Select Teeth
                        </label>
                        <ToothChart
                          selectedTeeth={item.selectedTeeth}
                          onSelectionChange={(teeth) => updateItem(index, { 
                            selectedTeeth: teeth,
                            toothNumbers: teeth.join(', ')
                          })}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {orderItems.length > 0 && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium text-gray-900 dark:text-white">Total</span>
                <span className="text-xl font-bold text-primary-600">{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          )}

          <div className="flex justify-between mt-6">
            <Button variant="secondary" onClick={() => setStep(1)}>
              <i className="fas fa-arrow-left mr-2"></i>
              Back
            </Button>
            <Button onClick={nextStep}>
              Next: Review
              <i className="fas fa-arrow-right ml-2"></i>
            </Button>
          </div>
        </Card>
      )}

      {/* Step 3: Review & Submit */}
      {step === 3 && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Review Order
          </h2>

          <div className="space-y-6">
            {/* Doctor Info */}
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Doctor</h3>
              <p className="font-medium text-gray-900 dark:text-white">{selectedDoctor?.name}</p>
              <p className="text-sm text-gray-500">{selectedDoctor?.displayId} • {selectedDoctor?.phone}</p>
            </div>

            {/* Patient & Order Info */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              <div>
                <span className="text-sm text-gray-500">Patient</span>
                <p className="font-medium text-gray-900 dark:text-white">{patientName || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Priority</span>
                <p className="font-medium text-gray-900 dark:text-white capitalize">{priority}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Received Date</span>
                <p className="font-medium text-gray-900 dark:text-white">{receivedDate ? new Date(receivedDate).toLocaleDateString() : 'Today'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Due Date</span>
                <p className="font-medium text-gray-900 dark:text-white">{new Date(dueDate).toLocaleDateString()}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Total Items</span>
                <p className="font-medium text-gray-900 dark:text-white">{orderItems.length}</p>
              </div>
            </div>

            {/* Items Summary */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Items</h3>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-2 text-left text-gray-600 dark:text-gray-300">Item</th>
                      <th className="px-4 py-2 text-left text-gray-600 dark:text-gray-300">Teeth</th>
                      <th className="px-4 py-2 text-center text-gray-600 dark:text-gray-300">Qty</th>
                      <th className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">Price</th>
                      <th className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderItems.map((item, index) => (
                      <tr key={index} className="border-t border-gray-100 dark:border-gray-700">
                        <td className="px-4 py-2 text-gray-900 dark:text-white">
                          <div>
                            <p className="font-medium">{item.catalogItem?.name || 'Unknown Item'}</p>
                            {item.shade && (
                              <p className="text-xs text-gray-500">Shade: {item.shade}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-gray-600 dark:text-gray-300">
                          {item.selectedTeeth.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {item.selectedTeeth.slice(0, 5).map((tooth) => (
                                <span key={tooth} className="px-1.5 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs rounded">
                                  {tooth}
                                </span>
                              ))}
                              {item.selectedTeeth.length > 5 && (
                                <span className="text-xs text-gray-500">+{item.selectedTeeth.length - 5} more</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center text-gray-600 dark:text-gray-300">{item.quantity}</td>
                        <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300">{formatCurrency(item.unitPrice)}</td>
                        <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-white">
                          {formatCurrency(item.quantity * item.unitPrice)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <td colSpan={4} className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                        Grand Total
                      </td>
                      <td className="px-4 py-3 text-right text-lg font-bold text-primary-600">
                        {formatCurrency(calculateTotal())}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {notes && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Special Instructions</h3>
                <p className="text-gray-900 dark:text-white">{notes}</p>
              </div>
            )}
          </div>

          {(mutation.isError || submitError) && (
            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-3">
                <i className="fas fa-exclamation-triangle text-red-500 mt-0.5"></i>
                <div>
                  <p className="font-medium text-red-700 dark:text-red-300">Failed to create work order</p>
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                    {submitError || 'An unexpected error occurred. Please try again.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between mt-6">
            <Button variant="secondary" onClick={() => setStep(2)}>
              <i className="fas fa-arrow-left mr-2"></i>
              Back
            </Button>
            <Button onClick={handleSubmit} loading={mutation.isPending}>
              <i className="fas fa-check mr-2"></i>
              Create Work Order
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}
